<template>
  <section
    id="how-it-works"
    class="relative overflow-hidden bg-white py-20 tablet:py-28"
  >
    <div
      class="pointer-events-none absolute bottom-[-200px] end-[-400px] hidden size-[946px] rounded-full bg-gold-primary-10 blur-[100px] tablet:block"
    ></div>
    <div class="container relative">
      <div class="mx-auto max-w-3xl text-center">
        <i18n-t
          :keypath="isUae ? 'landing.uae.onboarding.title' : 'landing.howItWorks.title'"
          scope="global"
          tag="h2"
          class="text-3xl font-bold tracking-tight tablet:text-[44px] tablet:leading-[1.1]"
        >
          <template #br><br /></template>
          <template #accent
            ><span class="text-gold-primary-120">{{ t('landing.howItWorks.titleAccent') }}</span></template
          >
        </i18n-t>
        <p
          v-if="!isUae"
          class="mt-4 text-base text-black-80 tablet:text-lg"
        >
          {{ t('landing.howItWorks.description') }}
        </p>
      </div>

      <div class="mx-auto mt-14 flex max-w-4xl flex-col gap-6 tablet:mt-16">
        <div
          v-for="(s, i) in steps"
          :key="i"
          class="grid items-start gap-4 tablet:grid-cols-[160px_80px_1fr] tablet:gap-6 [&>*]:min-w-0"
          :class="{ 'is-final': i === steps.length - 1 }"
        >
          <div class="tablet:text-end">
            <div class="text-xs font-bold uppercase tracking-wider text-black-60">{{ s.day }}</div>
            <div
              v-if="s.meta"
              class="text-xs text-black-60"
            >
              {{ s.meta }}
            </div>
          </div>
          <div class="relative flex justify-center">
            <div
              class="z-10 flex size-14 items-center justify-center rounded-full bg-gold-primary-120 text-xl font-bold text-black-100 tablet:size-16 tablet:text-2xl"
            >
              {{ i + 1 }}
            </div>
            <div
              v-if="i < steps.length - 1"
              class="absolute top-14 h-[calc(100%+24px)] w-px bg-gradient-to-b from-gold-primary-120 to-transparent tablet:top-16"
            ></div>
          </div>
          <div class="pt-1 tablet:pt-2">
            <div class="text-[11px] font-medium uppercase tracking-[0.12em] text-gold-primary-120 tablet:hidden">
              {{ s.mobileLabel }}
            </div>
            <h3 class="mt-1 text-lg font-bold tracking-tight text-black-100 tablet:text-xl">{{ s.title }}</h3>
            <p class="mt-2 text-sm text-black-80">{{ s.body }}</p>
            <div
              v-if="i === steps.length - 1"
              class="relative mt-4 h-20"
              aria-hidden="true"
            >
              <!-- sparkle 1 -->
              <svg
                class="absolute start-0 top-1 size-4 text-gold-primary-120 hms1"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 2 L13.8 10.2 L22 12 L13.8 13.8 L12 22 L10.2 13.8 L2 12 L10.2 10.2 Z"
                  fill="currentColor"
                />
              </svg>
              <!-- sparkle 2 -->
              <svg
                class="absolute end-4 top-6 size-3 text-gold-primary-100 hms2"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 2 L13.8 10.2 L22 12 L13.8 13.8 L12 22 L10.2 13.8 L2 12 L10.2 10.2 Z"
                  fill="currentColor"
                />
              </svg>
              <!-- banknote back -->
              <svg
                class="absolute start-8 top-2 h-12 w-20 -rotate-[14deg]"
                viewBox="0 0 120 64"
                fill="none"
              >
                <rect
                  x="2"
                  y="2"
                  width="116"
                  height="60"
                  rx="8"
                  fill="#FBE7C5"
                  stroke="#EDA436"
                  stroke-width="1.5"
                />
                <circle
                  cx="60"
                  cy="32"
                  r="14"
                  fill="none"
                  stroke="#D4A032"
                  stroke-width="1.5"
                />
                <text
                  x="60"
                  y="37"
                  text-anchor="middle"
                  font-family="Manrope, sans-serif"
                  font-size="13"
                  font-weight="800"
                  fill="#D4A032"
                >
                  AED
                </text>
                <path
                  d="M12 14 L20 14 M12 18 L24 18"
                  stroke="#EDA436"
                  stroke-width="1.2"
                  stroke-linecap="round"
                />
                <path
                  d="M108 50 L100 50 M108 46 L96 46"
                  stroke="#EDA436"
                  stroke-width="1.2"
                  stroke-linecap="round"
                />
              </svg>
              <!-- banknote front -->
              <svg
                class="absolute start-14 top-4 h-12 w-20 rotate-[8deg]"
                viewBox="0 0 120 64"
                fill="none"
              >
                <rect
                  x="2"
                  y="2"
                  width="116"
                  height="60"
                  rx="8"
                  fill="#fff"
                  stroke="#D4A032"
                  stroke-width="1.5"
                />
                <circle
                  cx="60"
                  cy="32"
                  r="15"
                  fill="none"
                  stroke="#D4A032"
                  stroke-width="1.5"
                />
                <text
                  x="60"
                  y="37"
                  text-anchor="middle"
                  font-family="Manrope, sans-serif"
                  font-size="14"
                  font-weight="900"
                  fill="#D4A032"
                >
                  AED
                </text>
                <path
                  d="M12 14 L22 14 M12 18 L26 18 M12 22 L20 22"
                  stroke="#D4A032"
                  stroke-width="1.3"
                  stroke-linecap="round"
                />
                <path
                  d="M108 50 L98 50 M108 46 L94 46 M108 42 L100 42"
                  stroke="#D4A032"
                  stroke-width="1.3"
                  stroke-linecap="round"
                />
              </svg>
              <!-- coin -->
              <svg
                class="absolute start-40 top-3 size-10 tablet:start-44"
                viewBox="0 0 48 48"
                fill="none"
              >
                <circle
                  cx="24"
                  cy="24"
                  r="22"
                  fill="#D4A032"
                  stroke="#B8860B"
                  stroke-width="1.5"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="17"
                  fill="none"
                  stroke="#1A1A1A"
                  stroke-width="1"
                  opacity="0.25"
                />
                <text
                  x="24"
                  y="30"
                  text-anchor="middle"
                  font-family="Manrope, sans-serif"
                  font-size="18"
                  font-weight="900"
                  fill="#1A1A1A"
                >
                  $
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-14 flex flex-col items-center gap-4 text-center">
        <NuxtLink
          :to="applyHref"
          @click="trackCtaClick('onboarding')"
          class="inline-flex h-14 w-full max-w-[325px] items-center justify-center gap-2 rounded-xl bg-black-100 px-7 text-base font-bold text-white transition-transform hover:-translate-y-px"
        >
          <span
            >{{ t(isUae ? 'landing.uae.onboarding.cta' : 'landing.shared.applyToJoin') }}
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >→</span
            ></span
          >
        </NuxtLink>
        <p class="max-w-lg text-sm text-black-60">
          {{ t(isUae ? 'landing.uae.onboarding.note' : 'landing.howItWorks.note') }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { SiteMarket } from '~/constants/market';
import { UAE_PARTNER_MAX_PERCENT_DEFAULT } from '~/constants/uaeModel';
const { t, te } = useI18n();
const { formatNumber } = useFormat();
const { totalProjects } = useLandingFacts();

const { market: marketProp } = defineProps<{ market?: SiteMarket }>();
const { track } = useLandingPage();
const isUae = computed(() => (marketProp ?? track.value) === 'uae');
const { copyParams: uaeCopy } = useUaeModel();

const STEP_KEYS = ['step1', 'step2', 'step3', 'step4', 'step5'] as const;

const stepMeta = (key: (typeof STEP_KEYS)[number]) =>
  te(`landing.howItWorks.${key}.meta`) ? t(`landing.howItWorks.${key}.meta`) : '';

// Step 3 names the live project count; without it the copy drops the number.
const stepBody = (key: (typeof STEP_KEYS)[number]) => {
  if (key !== 'step3') return t(`landing.howItWorks.${key}.body`);
  return totalProjects.value === null
    ? t('landing.howItWorks.step3.bodyFallback')
    : t('landing.howItWorks.step3.body', { count: formatNumber(totalProjects.value) });
};

// /uae: its own five steps, no day labels, model numbers from the UAE settings.
const uaeSteps = () =>
  STEP_KEYS.map((key) => ({
    day: '',
    meta: '',
    mobileLabel: '',
    title: t(`landing.uae.onboarding.steps.${key}.title`),
    body: t(`landing.uae.onboarding.steps.${key}.body`, {
      ...uaeCopy.value,
      partner: `${UAE_PARTNER_MAX_PERCENT_DEFAULT}%`,
    }),
  }));

const internationalSteps = () =>
  STEP_KEYS.map((key) => ({
    day: t(`landing.howItWorks.${key}.day`),
    meta: stepMeta(key),
    mobileLabel: [t(`landing.howItWorks.${key}.day`), stepMeta(key)].filter(Boolean).join(' · '),
    title: t(`landing.howItWorks.${key}.title`),
    body: stepBody(key),
  }));

const steps = computed(() => (isUae.value ? uaeSteps() : internationalSteps()));

const { signupHref: applyHref } = useSignupHref();
const { trackCtaClick } = useLandingTracking();
</script>
